import React from 'react'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import { FiberManualRecord } from '@material-ui/icons'
import cn from 'classnames'
import ListFromTable from 'components/common/ListFromTable'
import {
  jobWorkflowFetchTableData,
  jobWorkflowClearStateList,
  jobWorkflowPositionUpdate,
  jobWorkflowDelete,
  jobWorkflowChange,
  jobWorkflowCopyData,
} from 'reducers/jobsWorkflowAdminJob/reducer'
import { JOBS_WORKFLOW_ADMIN } from 'constants/appRouting'
import * as ROUTE from 'constants/appRouting'
import ActionCollection from 'types/table/ActionCollection'
import Action from 'types/table/Action'
import UrlHelper from 'helpers/url'
import { getFormattedDate, getDateFormat } from 'helpers/dates'
import ColumnCollection from 'types/table/ColumnCollection'
import Column from 'types/table/Column'
import RoleHelper from 'helpers/role'
import JobsWorkflowAdminPositionChange from '../JobsWorkflowAdminPositionChange'
import messages from '../translate'

class JobsWorkflowAdminJobList extends ListFromTable {
  constructor(props) {
    super(props)
    this.getTableData = props.jobWorkflowFetchTableData
    this.deleteEntity = props.jobWorkflowDelete
    this.clearReducerState = props.jobWorkflowClearStateList
    this.asyncData = true
    this.loadingData = true

    this.state = {
      confirmDialogData: {
        title: '',
        message: '',
      },
      openCopyDialog: false,
    }
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    const { licenseeId, tableData } = this.props
    const { licenseeId: nextLicenseeId, tableData: nextTableData } = nextProps

    if (nextLicenseeId !== licenseeId) {
      this.reloadTableData(ROUTE.JOB_WORKFLOWS)

      return false
    }

    if (tableData !== nextTableData) {
      this.loadingData = false
    }

    return super.shouldComponentUpdate(nextProps, nextState, nextContext)
  }

  getTableProps() {
    const {
      intl: { formatMessage },
      history: { push },
    } = this.props

    return {
      goBack: () => push(JOBS_WORKFLOW_ADMIN),
      title: formatMessage(messages.jobWorkflowList),
      showFilterBar: true,
      sorting: true,
      paging: true,
      searching: true,
      creating: true,
    }
  }

  getCreateButton() {
    const {
      intl: { formatMessage },
    } = this.props

    return {
      title: formatMessage(messages.createButton),
      link: ROUTE.JOB_WORKFLOWS_JOB_CREATE,
    }
  }

  getColumns() {
    const {
      intl: { formatMessage },
      jobWorkflowPositionUpdate,
      licensee: { usaDateFormat },
    } = this.props
    const columns = new ColumnCollection()
    const dateDisplay = getDateFormat(usaDateFormat)

    columns.add(new Column('id', 'ID', { sorting: true }))
    columns.add(
      new Column('position', formatMessage(messages.position), {
        sorting: true,
        render: ({ position, id }, rowIndex) => {
          const handlePositionChange = position => {
            jobWorkflowPositionUpdate({
              params: {
                type: 'job-workflows',
                id,
              },
              data: {
                position,
              },
              index: rowIndex,
            })
          }

          return (
            <JobsWorkflowAdminPositionChange
              position={position}
              onChange={handlePositionChange}
            />
          )
        },
      }),
    )
    columns.add(
      new Column('title', formatMessage(messages.title), { sorting: true }),
    )
    columns.add(
      new Column('date_created', formatMessage(messages.dateCreated), {
        sorting: true,
        render: ({ date_created }) =>
          getFormattedDate(date_created, dateDisplay),
      }),
    )
    columns.add(
      new Column('enabled', formatMessage(messages.active), {
        sorting: true,
        render: ({ enabled }) => (
          <FiberManualRecord
            className={cn('user-status-icon', { 'status-enabled': enabled })}
          />
        ),
      }),
    )

    return columns
  }

  getActions() {
    const {
      history: { push },
      intl: { formatMessage },
      jobWorkflowCopyData,
      roleLevel,
    } = this.props
    const actions = new ActionCollection()

    if (roleLevel >= RoleHelper.ROLE_ADMIN_LEVEL) {
      actions.add(
        new Action(
          'add_circle',
          formatMessage(messages.copyJobWorkflowToAnotherLicensee),
          ({ id }) => {
            this.onOpenCopyDialog()
            this.setState({
              copyDialogAction: jobWorkflowCopyData,
              copyDialogParams: { entityId: id },
            })
          },
        ),
      )
    }
    actions.add(
      new Action(
        'content_copy',
        formatMessage(messages.copyJobWorkflow),
        ({ id }) => {
          this.setState({
            confirmDialogData: {
              title: messages.copyJobWorkflowTitle,
              message: messages.copyJobWorkflowMessage,
            },
          })
          this.onOpenConfirmDialog(
            {
              entityId: id,
              psfs: this.psfs,
            },
            jobWorkflowCopyData,
          )
        },
      ),
    )
    actions.add(
      new Action('edit', formatMessage(messages.editJobWorkflow), ({ id }) => {
        push(UrlHelper.create(ROUTE.JOB_WORKFLOWS_JOB_EDIT, { id }))
      }),
    )
    actions.add(
      new Action(
        'delete',
        formatMessage(messages.deleteJobWorkflow),
        ({ id }) => {
          this.setState({
            confirmDialogData: {
              title: messages.deleteJobWorkflow,
              message: messages.deleteJobWorkflowMessage,
            },
          })
          this.onOpenConfirmDialog({
            entityId: id,
            psfs: this.psfs,
          })
        },
      ),
    )
    actions.add(
      new Action(
        'arrow_forward',
        formatMessage(messages.goToTasks),
        ({ id, title }) => {
          const queryParams = new URLSearchParams()

          queryParams.append('jobId', id)
          queryParams.append('jobTitle', title)

          push({
            pathname: ROUTE.JOB_WORKFLOW_TASKS,
            search: queryParams.toString(),
          })
        },
      ),
    )

    return actions
  }

  getConfirmDialogProps() {
    const {
      intl: { formatMessage },
    } = this.props
    const {
      confirmDialogData: {
        title: confirmDialogTitle,
        message: confirmDialogMessage,
      },
    } = this.state

    return {
      title: confirmDialogTitle ? formatMessage(confirmDialogTitle) : '',
      message: confirmDialogMessage ? formatMessage(confirmDialogMessage) : '',
    }
  }
}

export default connect(
  state => ({
    tableData: state.getIn(['jobWorkflow', 'tableData']),
    licenseeId: state.getIn(['profile', 'licenseeId']),
    licensee: state.getIn(['profile', 'profile', 'licensee']),
    roleLevel: state.getIn(['profile', 'roleLevel']),
  }),
  {
    jobWorkflowFetchTableData,
    jobWorkflowClearStateList,
    jobWorkflowPositionUpdate,
    jobWorkflowCopyData,
    jobWorkflowDelete,
    jobWorkflowChange,
  },
)(injectIntl(JobsWorkflowAdminJobList))
